import { useEffect, useMemo, useState } from 'react';
import { Button, Offcanvas } from 'react-bootstrap';
import Select, {
    type FormatOptionLabelMeta,
    type MultiValue,
    type StylesConfig,
} from 'react-select';
import { useTranslation } from 'react-i18next';
import {
    TaskTypeStatus,
    type LeadType,
    type ObjectType,
    type TaskTypeDirType,
    type TaskTypeStyleType,
    type UserType,
} from '@econtractor/types';
import { useAuth } from 'context/AuthContext';
import { isManagerUser } from 'utils/helpers/access/hasUserAccess';
import { useSettings } from 'context/SettingsContext';
import { useTheme } from 'context/ThemeContext';
import { selectStylesDark, selectStylesLigth } from 'utils/helpers/structs/selectStyles';
import { LeadSelected } from 'modules/lead';
import { ObjectSelected } from 'modules/objects';
import EToolTip from 'components/others/EToolTip';
import { UserSelected } from 'modules/user';
import { useTask } from '../../TaskContext';
import TaskStatusBadge from '../TaskStatusBadge';
import TaskTypeChip from '../TaskTypeChip';
import { tDirName } from 'utils/helpers/i18n/tDirName';
import vconst from 'utils/helpers/structs/vconst';
import SolarIcon from 'components/icons/SolarIcon';

/** У фільтрі задач: усі статуси заявок, крім Погоджено і Відмова */
const TASK_FILTER_LEAD_STATUSES = [
    vconst.leadNew,
    vconst.leadInWoork,
    vconst.leadNeedOffer,
    vconst.leadNeedsAttention,
    vconst.leadOffer,
];

/** У фільтрі задач: усі статуси об'єктів, крім Завершений і Відмінений */
const TASK_FILTER_OBJECT_STATUSES = [
    vconst.objectPanding,
    vconst.objectInWork,
    vconst.objectFinishedTeam,
];

interface FilterOption {
    value: number;
    label: string;
    kind: 'type' | 'status';
    icon?: string | null;
    colors?: TaskTypeStyleType | null;
}

const TASK_FILTER_STATUSES = [
    TaskTypeStatus.NEW,
    TaskTypeStatus.INWORK,
    TaskTypeStatus.COMPLETED,
    TaskTypeStatus.ACCEPTED,
    TaskTypeStatus.CANCELLED,
] as const;

/** Вище за Bootstrap offcanvas (~1045), щоб меню селекту було видно */
const MENU_Z_INDEX = 2100;

const buildSelectStyles = (dark: boolean): StylesConfig<FilterOption, true> => {
    const base = (dark ? selectStylesDark : selectStylesLigth) as StylesConfig<FilterOption, true>;

    return {
        ...base,
        control: (styles, state) => ({
            ...(base.control ? base.control(styles, state) : styles),
            minHeight: 38,
            fontSize: '0.8125rem',
            paddingBlock: 2,
        }),
        valueContainer: (styles, state) => ({
            ...(base.valueContainer ? base.valueContainer(styles, state) : styles),
            gap: 4,
            paddingTop: 2,
            paddingBottom: 2,
        }),
        indicatorsContainer: (styles) => ({
            ...styles,
            alignSelf: 'stretch',
        }),
        multiValue: () => ({
            display: 'flex',
            alignItems: 'center',
            backgroundColor: 'transparent',
            borderRadius: 0,
            margin: 0,
            padding: 0,
            maxWidth: '100%',
        }),
        multiValueLabel: () => ({
            padding: 0,
            fontSize: 'inherit',
            color: 'inherit',
            whiteSpace: 'nowrap',
        }),
        multiValueRemove: (styles) => ({
            ...styles,
            borderRadius: 6,
            padding: '0 4px',
            marginLeft: 2,
            color: dark ? '#9ca3af' : '#6b7280',
            ':hover': {
                backgroundColor: dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                color: dark ? '#fff' : '#111827',
            },
        }),
        menu: (styles, state) => ({
            ...(base.menu ? base.menu(styles, state) : styles),
            zIndex: MENU_Z_INDEX,
            fontSize: '0.8125rem',
            overflow: 'hidden',
        }),
        menuList: (styles, state) => ({
            ...(base.menuList ? base.menuList(styles, state) : styles),
            maxHeight: 240,
            paddingTop: 4,
            paddingBottom: 4,
        }),
        menuPortal: (styles) => ({
            ...styles,
            zIndex: MENU_Z_INDEX,
        }),
        option: (styles, state) => ({
            ...(base.option ? base.option(styles, state) : styles),
            fontSize: '0.8125rem',
            paddingTop: 8,
            paddingBottom: 8,
            paddingLeft: 10,
            paddingRight: 10,
            cursor: 'pointer',
            backgroundColor: state.isFocused
                ? dark
                    ? 'rgba(255,255,255,0.06)'
                    : '#F3F4F6'
                : state.isSelected
                  ? dark
                      ? 'rgba(70,128,255,0.18)'
                      : '#EEF4FF'
                  : 'transparent',
        }),
        placeholder: (styles) => ({
            ...styles,
            fontSize: '0.8125rem',
            color: dark ? '#8b95a1' : '#9ca3af',
        }),
    };
};

const formatTypeOption = (
    option: FilterOption,
    meta: FormatOptionLabelMeta<FilterOption>,
) => (
    <TaskTypeChip
        name={option.label}
        icon={option.icon}
        colors={option.colors}
        fontSize={meta.context === 'menu' ? '0.75rem' : '0.7rem'}
    />
);

const formatStatusOption = (option: FilterOption) => (
    <TaskStatusBadge status={option.value} />
);

type TaskFilterProps = {
    /** Статуси, які не показувати у фільтрі */
    excludeStatuses?: number[];
    /** Показати фільтр виконавця (UserSelected, одиночний вибір) */
    showExecutorFilter?: boolean;
    /** Показати фільтр контролера (UserSelected, одиночний вибір) */
    showControllerFilter?: boolean;
    /** Початковий виконавець для UI (snapshot з localStorage) */
    initialExecutorUser?: UserType | null;
    /** Початковий контролер для UI (snapshot з localStorage) */
    initialControllerUser?: UserType | null;
    initialLead?: LeadType | null;
    initialObject?: ObjectType | null;
    onExecutorUserChange?: (user: UserType | null) => void;
    onControllerUserChange?: (user: UserType | null) => void;
    onLeadChange?: (lead: LeadType | null) => void;
    onObjectChange?: (object: ObjectType | null) => void;
};

const snapshotToUser = (user: UserType | null | undefined): UserType | null => {
    if (!user?.ID) return null;
    return {
        ID: Number(user.ID),
        userName: String(user.userName || ''),
        userEmail: String(user.userEmail || ''),
        userPicture: String(user.userPicture || ''),
    } as UserType;
};

const copyIds = (ids: number[] | null | undefined): number[] | null =>
    ids?.length ? [...ids] : null;

const TaskFilter = ({
    excludeStatuses,
    showExecutorFilter = false,
    showControllerFilter = false,
    initialExecutorUser = null,
    initialControllerUser = null,
    initialLead = null,
    initialObject = null,
    onExecutorUserChange,
    onControllerUserChange,
    onLeadChange,
    onObjectChange,
}: TaskFilterProps) => {
    const { t } = useTranslation(['Tasks', 'common']);
    const { auth } = useAuth();
    const { darkTheme } = useTheme();
    const { localSettings } = useSettings();
    const {
        tasksStatus,
        setTasksStatus,
        tasksType,
        setTasksType,
        tasksExecutor,
        setTasksExecutor,
        tasksController,
        setTasksController,
        filterLeadId,
        setFilterLeadId,
        filterObjectId,
        setFilterObjectId,
        onlyPersonal,
        setOnlyPersonal,
        onlyMine,
        setOnlyMine,
        canFilterRelated,
        setPage,
    } = useTask();
    const canPickRelated = canFilterRelated && isManagerUser(auth?.user);
    const canShowOnlyMine = showExecutorFilter || showControllerFilter;
    const myUserId = Number(auth?.user?.ID || 0);
    const isOtherUser = (user: UserType | null | undefined) =>
        Boolean(user?.ID && Number(user.ID) !== myUserId);
    const isOnlyMineLocked = (executor: UserType | null, controller: UserType | null) =>
        isOtherUser(executor) || isOtherUser(controller);
    const [selectTheme, setSelectTheme] = useState(() => buildSelectStyles(Boolean(darkTheme)));
    const [show, setShow] = useState(false);
    const [executorUser, setExecutorUser] = useState<UserType | null>(() =>
        snapshotToUser(initialExecutorUser),
    );
    const [controllerUser, setControllerUser] = useState<UserType | null>(() =>
        snapshotToUser(initialControllerUser),
    );
    const [lead, setLead] = useState<LeadType | null>(() => (initialLead?.ID ? initialLead : null));
    const [object, setObject] = useState<ObjectType | null>(() =>
        initialObject?.ID ? initialObject : null,
    );
    const [draftTypes, setDraftTypes] = useState<number[] | null>(copyIds(tasksType));
    const [draftStatuses, setDraftStatuses] = useState<number[] | null>(copyIds(tasksStatus));
    const [draftExecutorUser, setDraftExecutorUser] = useState<UserType | null>(() =>
        snapshotToUser(initialExecutorUser),
    );
    const [draftControllerUser, setDraftControllerUser] = useState<UserType | null>(() =>
        snapshotToUser(initialControllerUser),
    );
    const [draftLead, setDraftLead] = useState<LeadType | null>(() =>
        initialLead?.ID ? initialLead : null,
    );
    const [draftObject, setDraftObject] = useState<ObjectType | null>(() =>
        initialObject?.ID ? initialObject : null,
    );
    const [draftOnlyPersonal, setDraftOnlyPersonal] = useState(Boolean(onlyPersonal));
    const [draftOnlyMine, setDraftOnlyMine] = useState(() =>
        isOnlyMineLocked(
            snapshotToUser(initialExecutorUser),
            snapshotToUser(initialControllerUser),
        )
            ? false
            : onlyMine !== false,
    );
    const [draftExecutorPickerOpen, setDraftExecutorPickerOpen] = useState(() =>
        Boolean(snapshotToUser(initialExecutorUser)),
    );
    const [draftControllerPickerOpen, setDraftControllerPickerOpen] = useState(() =>
        Boolean(snapshotToUser(initialControllerUser)),
    );
    const [draftLeadPickerOpen, setDraftLeadPickerOpen] = useState(() => Boolean(initialLead?.ID));
    const [draftObjectPickerOpen, setDraftObjectPickerOpen] = useState(() =>
        Boolean(initialObject?.ID),
    );
    const excludeSet = useMemo(
        () => (excludeStatuses?.length ? new Set(excludeStatuses.map(Number)) : null),
        [excludeStatuses],
    );

    useEffect(() => {
        setSelectTheme(buildSelectStyles(Boolean(darkTheme)));
    }, [darkTheme]);

    useEffect(() => {
        if (!tasksExecutor?.length && executorUser) {
            setExecutorUser(null);
            onExecutorUserChange?.(null);
        }
    }, [tasksExecutor, executorUser, onExecutorUserChange]);

    useEffect(() => {
        if (!tasksController?.length && controllerUser) {
            setControllerUser(null);
            onControllerUserChange?.(null);
        }
    }, [tasksController, controllerUser, onControllerUserChange]);

    useEffect(() => {
        if (!filterLeadId && lead) {
            setLead(null);
            onLeadChange?.(null);
        }
    }, [filterLeadId, lead, onLeadChange]);

    useEffect(() => {
        if (!filterObjectId && object) {
            setObject(null);
            onObjectChange?.(null);
        }
    }, [filterObjectId, object, onObjectChange]);

    const typeOptions = useMemo<FilterOption[]>(
        () =>
            ((localSettings?.DIRS?.TaskType as TaskTypeDirType[] | undefined) || []).map((tt) => ({
                value: Number(tt.ID),
                label: tDirName(t, tt.i18nKey, tt.taskTypeName) || String(tt.taskTypeName || tt.ID),
                kind: 'type' as const,
                icon: tt.taskTypeIcon || null,
                colors: tt.taslTypeJson || null,
            })),
        [localSettings?.DIRS?.TaskType, t],
    );

    const statusOptions = useMemo<FilterOption[]>(
        () =>
            TASK_FILTER_STATUSES.filter((statusId) => !excludeSet?.has(statusId)).map(
                (statusId) => ({
                    value: statusId,
                    label: t(`status.${statusId}`),
                    kind: 'status' as const,
                }),
            ),
        [t, excludeSet],
    );

    const selectedTypes = useMemo(
        () => typeOptions.filter((opt) => draftTypes?.includes(opt.value)),
        [typeOptions, draftTypes],
    );

    const selectedStatuses = useMemo(
        () => statusOptions.filter((opt) => draftStatuses?.includes(opt.value)),
        [statusOptions, draftStatuses],
    );

    const draftOnlyMineLocked = isOnlyMineLocked(draftExecutorUser, draftControllerUser);

    const hasActiveFilter = Boolean(
        tasksStatus?.length ||
            tasksType?.length ||
            tasksExecutor?.length ||
            tasksController?.length ||
            filterLeadId ||
            filterObjectId ||
            onlyPersonal ||
            (canShowOnlyMine && onlyMine === false),
    );

    const handleHide = () => setShow(false);

    const handleOpen = () => {
        const nextExecutor = snapshotToUser(executorUser);
        const nextController = snapshotToUser(controllerUser);
        const nextLead = lead?.ID ? lead : null;
        const nextObject = object?.ID ? object : null;
        setDraftTypes(copyIds(tasksType));
        setDraftStatuses(copyIds(tasksStatus));
        setDraftExecutorUser(nextExecutor);
        setDraftControllerUser(nextController);
        setDraftLead(nextLead);
        setDraftObject(nextObject);
        setDraftOnlyPersonal(Boolean(onlyPersonal));
        const nextOnlyMineLocked = isOnlyMineLocked(nextExecutor, nextController);
        setDraftOnlyMine(nextOnlyMineLocked ? false : onlyMine !== false);
        setDraftExecutorPickerOpen(Boolean(nextExecutor));
        setDraftControllerPickerOpen(Boolean(nextController));
        setDraftLeadPickerOpen(Boolean(nextLead?.ID));
        setDraftObjectPickerOpen(Boolean(nextObject?.ID));
        setShow(true);
    };

    const handleChangeTypes = (options: MultiValue<FilterOption>) => {
        const ids = options.map((o) => o.value);
        setDraftTypes(ids.length ? ids : null);
    };

    const handleChangeStatuses = (options: MultiValue<FilterOption>) => {
        const ids = options.map((o) => o.value);
        setDraftStatuses(ids.length ? ids : null);
    };

    const handleChangeExecutor = (user: UserType | null) => {
        setDraftExecutorUser(user);
        if (!user) setDraftExecutorPickerOpen(false);
        if (isOnlyMineLocked(user, draftControllerUser)) setDraftOnlyMine(false);
    };

    const handleChangeController = (user: UserType | null) => {
        setDraftControllerUser(user);
        if (!user) setDraftControllerPickerOpen(false);
        if (isOnlyMineLocked(draftExecutorUser, user)) setDraftOnlyMine(false);
    };

    const handleChangeLead = (next: LeadType | null) => {
        setDraftLead(next);
        if (!next) setDraftLeadPickerOpen(false);
        if (next) setDraftOnlyPersonal(false);
    };

    const handleChangeObject = (next: ObjectType | null) => {
        setDraftObject(next);
        if (!next) setDraftObjectPickerOpen(false);
        if (next) setDraftOnlyPersonal(false);
    };

    const handleChangeOnlyPersonal = (checked: boolean) => {
        setDraftOnlyPersonal(checked);
        if (checked) {
            setDraftLead(null);
            setDraftObject(null);
            setDraftLeadPickerOpen(false);
            setDraftObjectPickerOpen(false);
        }
    };

    const handleApply = () => {
        setTasksType(copyIds(draftTypes));
        setTasksStatus(copyIds(draftStatuses));

        if (showExecutorFilter) {
            setExecutorUser(draftExecutorUser);
            onExecutorUserChange?.(draftExecutorUser);
            setTasksExecutor(draftExecutorUser?.ID ? [Number(draftExecutorUser.ID)] : null);
        }

        if (showControllerFilter) {
            setControllerUser(draftControllerUser);
            onControllerUserChange?.(draftControllerUser);
            setTasksController(
                draftControllerUser?.ID ? [Number(draftControllerUser.ID)] : null,
            );
        }

        if (canShowOnlyMine) {
            const nextOnlyMineLocked = isOnlyMineLocked(
                showExecutorFilter ? draftExecutorUser : null,
                showControllerFilter ? draftControllerUser : null,
            );
            setOnlyMine(nextOnlyMineLocked ? false : Boolean(draftOnlyMine));
        }

        if (canFilterRelated) {
            const nextOnlyPersonal = draftOnlyPersonal;
            const nextLead = canPickRelated && !nextOnlyPersonal ? draftLead : null;
            const nextObject = canPickRelated && !nextOnlyPersonal ? draftObject : null;
            setLead(nextLead);
            onLeadChange?.(nextLead);
            setFilterLeadId(nextLead?.ID ? Number(nextLead.ID) : null);
            setObject(nextObject);
            onObjectChange?.(nextObject);
            setFilterObjectId(nextObject?.ID ? Number(nextObject.ID) : null);
            setOnlyPersonal(nextOnlyPersonal);
        }

        setPage(1);
        handleHide();
    };

    const handleReset = () => {
        setTasksType(null);
        setTasksStatus(null);
        setTasksExecutor(null);
        setTasksController(null);
        setFilterLeadId(null);
        setFilterObjectId(null);
        setOnlyPersonal(false);
        setOnlyMine(true);
        setExecutorUser(null);
        setControllerUser(null);
        setLead(null);
        setObject(null);
        onExecutorUserChange?.(null);
        onControllerUserChange?.(null);
        onLeadChange?.(null);
        onObjectChange?.(null);
        setPage(1);
        handleHide();
    };

    return (
        <>
            <EToolTip title={t('toolTaskFilter')}>
                <span className="d-inline-block">
                    <Button
                        className="fs-14 px-2 ms-1"
                        size="sm"
                        variant={hasActiveFilter ? 'danger' : 'primary'}
                        onClick={(e) => {
                            e.currentTarget.blur();
                            handleOpen();
                        }}
                    >
                        <SolarIcon name="funnel" className="fs-14 p-0" />
                    </Button>
                </span>
            </EToolTip>

            <Offcanvas
                show={show}
                onHide={handleHide}
                placement="end"
                backdrop
                backdropClassName="task-filter-offcanvas-backdrop"
                className="task-filter-offcanvas"
            >
                <Offcanvas.Header closeButton className="border-bottom">
                    <Offcanvas.Title>{t('toolTaskFilter')}</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                    <div className="off-filter-form d-flex flex-column h-100">
                        <div className="off-filter-content px-3">
                            <div className="mb-3">
                                <label className="form-label text-muted fs-12 mb-1">
                                    {t('filterType')}
                                </label>
                                <Select<FilterOption, true>
                                    isMulti
                                    isClearable
                                    isSearchable
                                    options={typeOptions}
                                    value={selectedTypes}
                                    onChange={handleChangeTypes}
                                    placeholder={t('filterType')}
                                    className="w-100 task-filter-select"
                                    classNamePrefix="task-filter-select"
                                    styles={selectTheme}
                                    formatOptionLabel={formatTypeOption}
                                    menuPortalTarget={
                                        typeof document !== 'undefined' ? document.body : null
                                    }
                                    menuPosition="fixed"
                                    menuShouldScrollIntoView={false}
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                />
                            </div>

                            <div className="mb-3">
                                <label className="form-label text-muted fs-12 mb-1">
                                    {t('filterStatus')}
                                </label>
                                <Select<FilterOption, true>
                                    isMulti
                                    isClearable
                                    isSearchable={false}
                                    options={statusOptions}
                                    value={selectedStatuses}
                                    onChange={handleChangeStatuses}
                                    placeholder={t('filterStatus')}
                                    className="w-100 task-filter-select"
                                    classNamePrefix="task-filter-select"
                                    styles={selectTheme}
                                    formatOptionLabel={formatStatusOption}
                                    menuPortalTarget={
                                        typeof document !== 'undefined' ? document.body : null
                                    }
                                    menuPosition="fixed"
                                    menuShouldScrollIntoView={false}
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                />
                            </div>

                            {showExecutorFilter || showControllerFilter || canFilterRelated ? (
                                <div className="mb-3">
                                    {showExecutorFilter ? (
                                        <div className="mb-2">
                                            {draftExecutorUser || draftExecutorPickerOpen ? (
                                                <UserSelected
                                                    value={draftExecutorUser}
                                                    onChange={handleChangeExecutor}
                                                    title={
                                                        <>
                                                            <SolarIcon
                                                                name="person"
                                                                className="me-1"
                                                            />
                                                            {t('labelExecutor')}
                                                        </>
                                                    }
                                                    maxHeight={180}
                                                    smallMode
                                                    className="mb-0"
                                                    rightElement={
                                                        draftExecutorPickerOpen &&
                                                        !draftExecutorUser ? (
                                                            <button
                                                                type="button"
                                                                className="btn btn-link p-0 align-baseline fs-12"
                                                                onClick={() =>
                                                                    setDraftExecutorPickerOpen(
                                                                        false,
                                                                    )
                                                                }
                                                            >
                                                                {t('filterCancelPick')}
                                                            </button>
                                                        ) : null
                                                    }
                                                />
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="btn btn-link p-0 align-baseline fs-12"
                                                    onClick={() =>
                                                        setDraftExecutorPickerOpen(true)
                                                    }
                                                >
                                                    <SolarIcon
                                                        name="person"
                                                        className="me-1"
                                                    />
                                                    {t('filterPickExecutor')}
                                                </button>
                                            )}
                                        </div>
                                    ) : null}

                                    {showControllerFilter ? (
                                        <div className="mb-2">
                                            {draftControllerUser || draftControllerPickerOpen ? (
                                                <UserSelected
                                                    value={draftControllerUser}
                                                    onChange={handleChangeController}
                                                    title={
                                                        <>
                                                            <SolarIcon
                                                                name="clipboard-check"
                                                                className="me-1"
                                                            />
                                                            {t('labelController')}
                                                        </>
                                                    }
                                                    maxHeight={180}
                                                    smallMode
                                                    className="mb-0"
                                                    rightElement={
                                                        draftControllerPickerOpen &&
                                                        !draftControllerUser ? (
                                                            <button
                                                                type="button"
                                                                className="btn btn-link p-0 align-baseline fs-12"
                                                                onClick={() =>
                                                                    setDraftControllerPickerOpen(
                                                                        false,
                                                                    )
                                                                }
                                                            >
                                                                {t('filterCancelPick')}
                                                            </button>
                                                        ) : null
                                                    }
                                                />
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="btn btn-link p-0 align-baseline fs-12"
                                                    onClick={() =>
                                                        setDraftControllerPickerOpen(true)
                                                    }
                                                >
                                                    <SolarIcon
                                                        name="clipboard-check"
                                                        className="me-1"
                                                    />
                                                    {t('filterPickController')}
                                                </button>
                                            )}
                                        </div>
                                    ) : null}

                                    {draftExecutorUser && draftControllerUser ? (
                                        <div
                                            className="text-muted mb-2"
                                            style={{ fontSize: '0.65rem', lineHeight: 1.35 }}
                                        >
                                            {t('filterUsersOrHint')}
                                        </div>
                                    ) : null}

                                    {canPickRelated && !draftOnlyPersonal ? (
                                        <>
                                            {!draftObject && !draftObjectPickerOpen ? (
                                                <div className="mb-2">
                                                    {draftLead || draftLeadPickerOpen ? (
                                                        <LeadSelected
                                                            value={draftLead}
                                                            onChange={handleChangeLead}
                                                            title={t('labelLead')}
                                                            maxHeight={180}
                                                            className="mb-0"
                                                            statuses={TASK_FILTER_LEAD_STATUSES}
                                                            rightElement={
                                                                draftLeadPickerOpen &&
                                                                !draftLead ? (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-link p-0 align-baseline fs-12"
                                                                        onClick={() =>
                                                                            setDraftLeadPickerOpen(
                                                                                false,
                                                                            )
                                                                        }
                                                                    >
                                                                        {t('filterCancelPick')}
                                                                    </button>
                                                                ) : null
                                                            }
                                                        />
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            className="btn btn-link p-0 align-baseline fs-12"
                                                            onClick={() =>
                                                                setDraftLeadPickerOpen(true)
                                                            }
                                                        >
                                                            <SolarIcon
                                                                name="person-lines-fill"
                                                                className="me-1"
                                                            />
                                                            {t('filterPickLead')}
                                                        </button>
                                                    )}
                                                </div>
                                            ) : null}

                                            {!draftLead && !draftLeadPickerOpen ? (
                                                <div className="mb-2">
                                                    {draftObject || draftObjectPickerOpen ? (
                                                        <ObjectSelected
                                                            value={draftObject}
                                                            onChange={handleChangeObject}
                                                            title={t('labelObject')}
                                                            maxHeight={180}
                                                            className="mb-0"
                                                            statuses={TASK_FILTER_OBJECT_STATUSES}
                                                            rightElement={
                                                                draftObjectPickerOpen &&
                                                                !draftObject ? (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-link p-0 align-baseline fs-12"
                                                                        onClick={() =>
                                                                            setDraftObjectPickerOpen(
                                                                                false,
                                                                            )
                                                                        }
                                                                    >
                                                                        {t('filterCancelPick')}
                                                                    </button>
                                                                ) : null
                                                            }
                                                        />
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            className="btn btn-link p-0 align-baseline fs-12"
                                                            onClick={() =>
                                                                setDraftObjectPickerOpen(true)
                                                            }
                                                        >
                                                            <SolarIcon
                                                                name="building"
                                                                className="me-1"
                                                            />
                                                            {t('filterPickObject')}
                                                        </button>
                                                    )}
                                                </div>
                                            ) : null}
                                        </>
                                    ) : null}

                                    {canFilterRelated ? (
                                        <div className="form-check form-switch mb-2">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                id="task-filter-only-personal"
                                                checked={draftOnlyPersonal}
                                                onChange={(e) =>
                                                    handleChangeOnlyPersonal(
                                                        e.currentTarget.checked,
                                                    )
                                                }
                                            />
                                            <label
                                                className="form-check-label"
                                                htmlFor="task-filter-only-personal"
                                            >
                                                <span className="d-block fs-12">
                                                    {t('filterOnlyPersonal')}
                                                </span>
                                                <span
                                                    className="d-block text-muted"
                                                    style={{
                                                        fontSize: '0.65rem',
                                                        lineHeight: 1.35,
                                                    }}
                                                >
                                                    {t('filterOnlyPersonalHint')}
                                                </span>
                                            </label>
                                        </div>
                                    ) : null}

                                    {canShowOnlyMine ? (
                                        <div
                                            className={`form-check form-switch mb-0${
                                                draftOnlyMineLocked ? ' opacity-50' : ''
                                            }`}
                                        >
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                id="task-filter-only-mine"
                                                checked={draftOnlyMine && !draftOnlyMineLocked}
                                                disabled={draftOnlyMineLocked}
                                                onChange={(e) =>
                                                    setDraftOnlyMine(e.currentTarget.checked)
                                                }
                                            />
                                            <label
                                                className="form-check-label"
                                                htmlFor="task-filter-only-mine"
                                            >
                                                <span className="d-block fs-12">
                                                    {t('filterOnlyMine')}
                                                </span>
                                                <span
                                                    className="d-block text-muted"
                                                    style={{
                                                        fontSize: '0.65rem',
                                                        lineHeight: 1.35,
                                                    }}
                                                >
                                                    {t('filterOnlyMineHint')}
                                                </span>
                                            </label>
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>
                        <div className="p-3 offcanvas-footer off-filter-footer">
                            <div className="d-flex justify-content-end gap-2">
                                <Button variant="secondary" onClick={handleReset}>
                                    {t('common:buttons.reset')}
                                </Button>
                                <Button
                                    variant="info"
                                    className="min100px"
                                    onClick={handleApply}
                                >
                                    {t('common:buttons.apply')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </Offcanvas.Body>
            </Offcanvas>
        </>
    );
};

export default TaskFilter;
